import Button from '@atlaskit/button/standard-button';
import { FieldTextStateless } from '@atlaskit/field-text';
import Spinner from '@atlaskit/spinner';
import React, { Fragment } from 'react';

import logger from '../../../../app/logger';
import { Dialog } from '../../../../base/dialog';
import { translate } from '../../../../base/i18n';
import {
    Icon,
    IconCancelSelection,
    IconPlusCircle
} from '../../../../base/icons';
import { Switch } from '../../../../base/react';
import { connect } from '../../../../base/redux';
import {
    GOOGLE_API_STATES,
    GoogleSignInButton,
    loadGoogleAPI,
    requestAvailableYouTubeBroadcasts,
    requestLiveStreamsForYouTubeBroadcast,
    showAccountSelection,
    signIn,
    updateProfile
} from '../../../../google-api';
import StartRecordingDialogContent from '../../Recording/StartRecordingDialogContent';
import AbstractStartLiveStreamDialog, {
    type Props as AbstractProps,
    _mapStateToProps as _abstractMapStateToProps
} from '../AbstractStartLiveStreamDialog';

import StreamKeyPicker from './StreamKeyPicker';

const COLOR_WARNING = '#FFC107';
const COLOR_LIGHT_GREY = '#f5f5f5';

const _webStyles = {
    infoSection: { display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        padding: '10px',
        backgroundColor: COLOR_WARNING,
        width: '100%',
        borderRadius: '5px' },
    marginBottom20: { marginBottom: '20px' },
    toggleRow: { display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        padding: '10px',
        backgroundColor: COLOR_LIGHT_GREY,
        borderRadius: '5px' },
    labelBold: { marginLeft: '10px',
        fontWeight: 'bold' },
    marginLeft20Mb15: { marginLeft: '20px',
        marginBottom: '15px' },
    headerRow: { display: 'flex',
        alignItems: 'center',
        marginBottom: '15px',
        gap: '10px',
        fontWeight: 'bold',
        padding: '10px 0',
        borderBottom: '1px solid #e0e0e0' },
    minWidth150: { minWidth: '150px' },
    flex1: { flex: 1 },
    actionCol: { width: '60px',
        display: 'flex',
        justifyContent: 'center' },
    cursorPointer: { cursor: 'pointer' },
    streamRow: { display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        gap: '10px' },
    inlineFlex: { display: 'inline-flex',
        width: '448px' },
    paddingTop24Left12: { paddingTop: '24px',
        paddingLeft: '12px' },
    loaderContainer: { alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        padding: '12px 0' },
    width90: { width: '90%' }
};


type Props = AbstractProps & {

  /**
   * Invoked to obtain translated strings.
   */
  t: Function,

  /**
   * The ID for the Google client application used for making stream key
   * related requests.
   */
  _googleApiApplicationClientID: string,
};

/**
 * A React Component for requesting a YouTube stream key to use for live
 * streaming of the current conference.
 *
 * @extends Component
 */
class StartLiveStreamDialog extends AbstractStartLiveStreamDialog<Props> {
    /**
   * Initializes a new {@code StartLiveStreamDialog} instance.
   *
   * @param {Props} props - The React {@code Component} props to initialize
   * the new {@code StartLiveStreamDialog} instance with.
   */
    constructor(props: Props) {
        super(props);
        this.state = {
            ...this.state,
            streamRows: [ { id: 1,
                selectedOption: '',
                textValue: '' } ],
            recordingEnabled: false,
            liveStreamingEnabled: false,
            autofilldetailesreendered: false
        };

        // Bind event handlers so they are only bound once per instance.
        this._onGetYouTubeBroadcasts = this._onGetYouTubeBroadcasts.bind(this);
        this._onInitializeGoogleApi = this._onInitializeGoogleApi.bind(this);
        this._onGoogleSignIn = this._onGoogleSignIn.bind(this);
        this._onRequestGoogleSignIn = this._onRequestGoogleSignIn.bind(this);
        this._onYouTubeBroadcastIDSelected
      = this._onYouTubeBroadcastIDSelected.bind(this);
        this._onToggleStream = this._onToggleStream.bind(this);
        this._onToggleRecording = this._onToggleRecording.bind(this);
        this._onToggleLiveStreaming = this._onToggleLiveStreaming.bind(this);
    }

    /**
   * Implements {@link Component#componentDidMount()}. Invoked immediately
   * after this component is mounted.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentDidMount() {
        super.componentDidMount();

        // if (this.props._googleApiApplicationClientID) {
        //     this._onInitializeGoogleApi();
        // }

        // Initialize toggle states based on existing streamEnabledWeb data
        if (this.state.streamEnabledWeb && this.state.streamEnabledWeb.length > 0) {
            const hasRecording = this.state.streamEnabledWeb.some(item => item.isRec === true);
            const hasLiveStreaming = this.state.streamEnabledWeb.some(item => item.isRec !== true);

            // Filter out recording items for streamRows (only live streaming)
            const liveStreamItems = this.state.streamEnabledWeb.filter(item => item.isRec !== true);

            if (liveStreamItems.length > 0
        && (!this.state.streamRows || this.state.streamRows.length === 0
         || (this.state.streamRows.length === 1 && !this.state.streamRows[0].selectedOption))) {

                const rowsFromStreamEnabled = liveStreamItems.map((item, index) => {
                    return {
                        id: Date.now() + index,
                        selectedOption: item.title || '',
                        textValue: item.value || ''
                    };
                });

                this.setState({
                    streamRows: rowsFromStreamEnabled,
                    recordingEnabled: hasRecording,
                    liveStreamingEnabled: hasLiveStreaming,
                    recordingO: hasRecording // Sync with parent state
                });
            } else {
                this.setState({
                    recordingEnabled: hasRecording,
                    liveStreamingEnabled: hasLiveStreaming,
                    recordingO: hasRecording // Sync with parent state
                });
            }
        }
    }

    /**
   * Toggles the  Dialog.
   *
   * @returns {void}
   */
    toggleDialog = () => this.setState({ dialogOpen: !this.state.dialogOpen });

    /**
   * Content Created from the Data in the Interface Config.js.
   *
   * @returns {React#Element}
   */
    content = () => {
        const { liveStreamTools } = this.state;
        const { t, _maxLiveStreamsAllowed, _confSettings } = this.props;

        // Calculate max live streams based on recording state
        const maxLiveStreams = this.state.recordingEnabled ? _maxLiveStreamsAllowed - 1 : _maxLiveStreamsAllowed;

        return (
            <>
                <span
                    id = 'live-stream-info-section'
                    style = { _webStyles.infoSection }>
                    {_confSettings?.ENABLE_RECORDING === 1 ? t('info.liveSreamandRecordingInfo', { number: _maxLiveStreamsAllowed,
                        num: _maxLiveStreamsAllowed - 1 }) : t('info.liveStreamingInfo', { number: _maxLiveStreamsAllowed })}
                </span>

                {/* Recording Toggle Section */}
                <div
                    className = 'toggle-section'
                    style = { _webStyles.marginBottom20 }>
                    {_confSettings?.ENABLE_RECORDING === 1 && <div
                        style = { _webStyles.toggleRow }>
                        <Switch
                            onValueChange = { this._onToggleRecording }
                            value = { this.state.recordingEnabled } />
                        <span
                            style = { _webStyles.labelBold }>
                            {t('dialog.recording')}
                        </span>
                    </div>}

                    {/* Recording Content */}
                    {_confSettings?.ENABLE_RECORDING === 1 && this.state.recordingEnabled && (
                        <div
                            style = { _webStyles.marginLeft20Mb15 }>
                            {this.addRecordString()}
                        </div>
                    )}
                </div>

                {/* Live Streaming Toggle Section */}
                <div
                    className = 'toggle-section'
                    style = { _webStyles.marginBottom20 }>
                    <div
                        style = { _webStyles.toggleRow }>
                        <Switch
                            onValueChange = { this._onToggleLiveStreaming }
                            value = { this.state.liveStreamingEnabled } />
                        <span
                            style = { _webStyles.labelBold }>
                            {t('dialog.liveStreaming')}
                        </span>
                    </div>

                    {/* Live Streaming Content */}
                    {this.state.liveStreamingEnabled && (
                        <div className = 'stream-configuration'>
                            {/* Header Row */}
                            <div
                                className = 'stream-configuration-header'
                                style = { _webStyles.headerRow }>
                                <div style = { _webStyles.minWidth150 }>{t('dialog.streamingPlatforms')}</div>
                                <div style = { _webStyles.flex1 }>{t('dialog.streamKey')}</div>
                                <div
                                    style = { _webStyles.actionCol }>
                                    {/* Plus Button - Only show if under max limit */}
                                    {this.state.streamRows.length < maxLiveStreams && (
                                        <div
                                            className = 'stream-configuration-icon'
                                            onClick = { () => {
                                                this.setState(prevState => {
                                                    return {
                                                        streamRows: [
                                                            ...prevState.streamRows,
                                                            {
                                                                id: Date.now(),
                                                                selectedOption: '',
                                                                textValue: ''
                                                            }
                                                        ]
                                                    };
                                                });
                                            } }
                                            style = { _webStyles.cursorPointer }>
                                            <Icon
                                                size = { 16 }
                                                src = { IconPlusCircle } />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {this.state.streamRows.slice(0, maxLiveStreams).map((row, index) =>

                            // Find the corresponding tool for this row (only live streaming tools)

                                (
                                    <div
                                        className = 'stream-configuration-row'
                                        key = { row.id }
                                        style = { _webStyles.streamRow }>
                                        {/* Select Dropdown - Only Live Stream Tools */}
                                        <select
                                            className = 'stream-configuration-select'
                                            onChange = { e => {
                                                const selectedTitle = e.target.value;

                                                this.setState(prevState => {
                                                    return {
                                                        streamRows: prevState.streamRows.map((item, i) => {
                                                            if (i === index) {
                                                                return {
                                                                    ...item,
                                                                    selectedOption: selectedTitle,
                                                                    textValue: ''
                                                                };
                                                            }

                                                            return item;
                                                        })
                                                    };
                                                });
                                            } }
                                            style = { _webStyles.minWidth150 }
                                            value = { row.selectedOption }>
                                            <option value = ''>{t('dialog.selectTool')}</option>

                                            {/* Only Live Stream Tools */}
                                            {liveStreamTools
                        && liveStreamTools.filter(item => item.enabled).length
                          > 0 && (
                                                <>
                                                    {liveStreamTools
                              .filter(item => item.enabled)
                              .map((item, i) => (
                                  <option
                                      key = { `stream-${i}` }
                                      value = { item.title }>
                                      {item.title}
                                  </option>
                              ))}
                                                </>
                                            )}
                                        </select>

                                        {/* Text Field - Fixed to sync with streamEnabledWeb */}
                                        <FieldTextStateless
                                            autoFocus = { false }
                                            compact = { true }
                                            disabled = { !row.selectedOption }
                                            isSpellCheckEnabled = { false }
                                            name = { `streamKey${row.id}` }
                                            onChange = { e => {
                                                // Update streamRows
                                                this.setState(prevState => {
                                                    return {
                                                        streamRows: prevState.streamRows.map((item, i) => {
                                                            if (i === index) {
                                                                return { ...item,
                                                                    textValue: e.target.value };
                                                            }

                                                            return item;
                                                        })
                                                    };
                                                });

                                                // Sync with streamEnabledWeb like in your working component
                                                if (row.selectedOption) {
                                                    this.setState(prevState => {
                                                        const streamsKey = prevState.streamEnabledWeb.some(
                          it => it?.title === row.selectedOption
                                                        );

                                                        if (streamsKey) {
                                                            return {
                                                                ...prevState,
                                                                streamEnabledWeb: prevState.streamEnabledWeb.map(
                              prv => {
                                  if (prv?.title === row.selectedOption) {
                                      prv.value = e.target.value;
                                  }

                                  return prv;
                              }
                                                                )
                                                            };
                                                        }

                                                        return {
                                                            ...prevState,
                                                            streamEnabledWeb: [
                                                                ...prevState.streamEnabledWeb,
                                                                {
                                                                    title: row.selectedOption,
                                                                    value: e.target.value
                                                                }
                                                            ]
                                                        };
                                                    });
                                                }
                                            } }
                                            placeholder = {
                                                row.selectedOption === 'LinkedIn'
                                                    ? `Enter ${row.selectedOption} stream Key with url`
                                                    : row.selectedOption
                                                        ? `Enter ${row.selectedOption} stream Key`
                                                        : t('dialog.livestreamingtool')
                                            }
                                            shouldFitContainer = { true }
                                            style = { _webStyles.flex1 }
                                            value = {
                                                this.state?.streamEnabledWeb.find(
                      it => it?.title === row.selectedOption
                                                )?.value
                    ?? row.textValue
                    ?? ''
                                            } />

                                        {/* Delete Button (only show if more than one row) */}
                                        {this.state.streamRows.length > 1 && (
                                            <div
                                                className = 'stream-configuration-icon'
                                                onClick = { () => {
                                                    const rowToDelete = this.state.streamRows[index];

                                                    // Remove from streamRows
                                                    this.setState(prevState => {
                                                        return {
                                                            streamRows: prevState.streamRows.filter(
                              (_, i) => i !== index
                                                            )
                                                        };
                                                    });

                                                    // Also remove from streamEnabledWeb if it exists
                                                    if (rowToDelete.selectedOption) {
                                                        this.setState(prevState => {
                                                            return {
                                                                streamEnabledWeb: prevState.streamEnabledWeb.filter(
                                item => item.title !== rowToDelete.selectedOption
                                                                )
                                                            };
                                                        });
                                                    }
                                                } }>
                                                <Icon
                                                    size = { 10 }
                                                    src = { IconCancelSelection } />
                                            </div>
                                        )}
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </>
        );
    };

    /**
   * Form for Adding your Stream Key.
   * @param {string} title - Title
   *  @param {i} - index
   * @returns {React#Element}
   */
    formStreamKey = (title, i) => (
        <div
            key = { i }
            style = { _webStyles.inlineFlex }>
            <FieldTextStateless
                autoFocus = { true }
                compact = { true }
                isSpellCheckEnabled = { false }
                name = 'streamIdFB'
                okDisabled = { false }
                onChange = { e => {
                    //   this.setState({
                    //       streamKeys: [

                    //           [`streamKey${parseInt(i) + 1}`]: e.target.value
                    //       ]
                    //   }
                    //   );
                    this.setState(prevState => {
                        const streamsKey = prevState.streamEnabledWeb.some(
              it => it?.title === title
                        );

                        if (streamsKey) {
                            return {
                                ...prevState,
                                streamEnabledWeb: prevState.streamEnabledWeb.map(prv => {
                                    if (prv?.title === title) {
                                        prv.value = e.target.value;
                                    }

                                    return prv;
                                })
                            };
                        }

                        return {
                            ...prevState,
                            streamEnabledWeb: prevState.streamEnabledWeb.push({
                                title,
                                value: e.target.value
                            })
                        };
                    });
                } }
                placeholder = {
                    title === 'LinkedIn'
                        ? `Enter ${title} stream Key with url`
                        : `Enter ${title} stream Key`
                }
                shouldFitContainer = { true }
                type = 'text'
                value = {
                    this.state?.streamEnabledWeb.find(it => it?.title === title)
            ?.value ?? undefined
                } />
            <div
                style = { _webStyles.paddingTop24Left12 }>
                <Button
                    appearance = 'primary'
                    onClick = { () =>
                        this.setState(prevState => {
                            return {
                                streamEnabledWeb: prevState.streamEnabledWeb?.some(
                it => it?.title === title && it.hasOwnProperty('open')
                                )
                                    ? prevState.streamEnabledWeb.map(it => {
                                        if (it.title === title) {
                                            it.open = !it.open;
                                        }

                                        return it;
                                    })
                                    : prevState.streamEnabledWeb
                            };
                        })
                    }>
          Save
                </Button>
            </div>
        </div>
    );

    /**
   * Adding a Recording Dialog.
   *
   * @returns {React#Element}
   */
    addRecordString = () => {
        const {
            isLoaded,
            isTokenValid,
            isValidating,
            isAws,
            oneDriveSpaceLeft,
            oneDriveSpaceTotal,
            oneDriveSpaceUsed,
            oneDriveUserEmail,
            oneDriveUserName,
            selectedRecordingService,
            sharingEnabled,
            spaceLeft,
            userName
        } = this.state;
        const {
            _fileRecordingsServiceEnabled,
            _fileRecordingsServiceSharingEnabled
        } = this.props;
        const storageType = this.state.recTypeData?.storage;
        const { s3_free_space = null, s3_used_space = null } = this.state?.recTypeData ?? {};
        const isStorageFull = this._isStorageFullForSelectedRecordingService();

        if (isLoaded) {
            return (
                <div style = { _webStyles.loaderContainer }>
                    <Spinner size = 'large' />
                </div>
            );
        }

        return (
            <div style = { _webStyles.width90 }>
                <StartRecordingDialogContent
                    fileRecordingsServiceEnabled = { _fileRecordingsServiceEnabled }
                    fileRecordingsServiceSharingEnabled = {
                        _fileRecordingsServiceSharingEnabled
                    }
                    integrationsEnabled = { this._areIntegrationsEnabled() }
                    isAws = { isAws }
                    isStorageFull = { isStorageFull }
                    isTokenValid = { isTokenValid }
                    isValidating = { isValidating }
                    onChange = { this._onSelectedRecordingServiceChanged }
                    onSharingSettingChanged = { this._onSharingSettingChanged }
                    oneDriveSpaceLeft = { oneDriveSpaceLeft }
                    oneDriveSpaceTotal = { oneDriveSpaceTotal }
                    oneDriveSpaceUsed = { oneDriveSpaceUsed }
                    oneDriveUserEmail = { oneDriveUserEmail }
                    oneDriveUserName = { oneDriveUserName }
                    selectedRecordingService = { selectedRecordingService }
                    sharingSetting = { sharingEnabled }
                    spaceLeft = { spaceLeft }
                    storageSpace = {{
                        s3_free_space,
                        s3_used_space
                    }}
                    storageType = { storageType }
                    userName = { userName } />
            </div>
        );
    };

    /**
   * Implements {@code Component}'s render.
   *
   * @inheritdoc
   */
    render() {
        const { t, _googleApiApplicationClientID, _confSettings } = this.props;

        return (
            <>
                <Dialog
                    cancelKey = 'dialog.Cancel'
                    disableOverlay = { true }
                    okKey = {
                        this.state.recordingEnabled && this.state.liveStreamingEnabled
                            ? t('dialog.startLiveStreamRecording')
                            : this.state.recordingEnabled
                                ? t('dialog.startRecording')
                                : this.state.liveStreamingEnabled
                                    ? t('dialog.startLiveStream')
                                    : 'Start'
                    }
                    onCancel = { this._onCancel }
                    onSubmit = { this._onSubmit }
                    submitDisabled = { this._onDisable() }
                    titleKey = { _confSettings?.ENABLE_RECORDING === 1 ? 'liveStreaming.start' : 'welcomepage.header.liveStreaming' }
                    width = { '400px' }>
                    <div className = 'live-stream-dialog'>
                        {_googleApiApplicationClientID ? this._renderYouTubePanel() : null}
                        <div id = 'live-stream-section'>
                            <div>
                                {this.content()}
                            </div>
                        </div>
                    </div>
                </Dialog>
            </>
        );
    }

    _areIntegrationsEnabled: () => boolean;
    _onSelectedRecordingServiceChanged: (string) => void;
    _onSharingSettingChanged: () => void;

    /**
   * Callback to be invoked when the user toggles recording on or off.
   * @returns {void}
   */
    _onToggleRecording = () => {
        const newRecordingState = !this.state.recordingEnabled;

        // Check if trying to enable recording with 2 live streams
        if (newRecordingState && this.state.liveStreamingEnabled) {
            const activeLiveStreams = this.state.streamRows.filter(
        row => row.selectedOption && row.textValue && row.textValue.trim() !== ''
            ).length;

            if (activeLiveStreams >= this.props._maxLiveStreamsAllowed) {
                const el = document.getElementById('live-stream-info-section');

                if (el) {
                    el.classList.add('horizontal-shake');
                    el.addEventListener('animationend', () => {
                        el.classList.remove('shake');
                    });
                    setTimeout(() => {
                        el.classList.remove('horizontal-shake');
                    }, 1000);
                }

                return;
            }
        }

        this.setState({
            recordingEnabled: newRecordingState,
            recordingO: newRecordingState, // This is the key state used by parent component
            ...newRecordingState ? { isLoaded: true } : {}
        }, () => {
            // Re-fetch recording config when toggling recording ON
            if (newRecordingState) {
                this._fetchRecordingConfig();
            }

            // Update streamEnabledWeb to include/exclude recording
            if (newRecordingState) {
                // Add recording to streamEnabledWeb if not already present
                const hasRecording = this.state.streamEnabledWeb.some(item => item.isRec === true);

                if (!hasRecording && this.state.recTools && this.state.recTools.length > 0) {
                    const recordingTool = this.state.recTools[0];

                    this.setState(prevState => {
                        return {
                            streamEnabledWeb: [
                                ...prevState.streamEnabledWeb,
                                {
                                    title: recordingTool.title,
                                    toggle: true,
                                    open: false,
                                    value: undefined,
                                    isRec: true
                                }
                            ]
                        };
                    });
                }

                // If recording is enabled, limit to 1 live stream
                const activeLiveStreams = this.state.streamRows.filter(
          row => row.selectedOption && row.textValue && row.textValue.trim() !== ''
                );

                if (activeLiveStreams.length > 1) {
                    // Keep only the first active stream
                    this.setState(prevState => {
                        return {
                            streamRows: [ activeLiveStreams[0] ],
                            streamEnabledWeb: prevState.streamEnabledWeb.filter(
              item => item.isRec === true || item.title === activeLiveStreams[0].selectedOption
                            )
                        };
                    });
                }
            } else {
                // Remove recording from streamEnabledWeb
                this.setState(prevState => {
                    return {
                        streamEnabledWeb: prevState.streamEnabledWeb.filter(item => item.isRec !== true)
                    };
                });
            }
        });
    };

    /**
   * Callback to be invoked when the user toggles live streaming on or off.
   * @returns {void}
   */
    _onToggleLiveStreaming = () => {
        const newLiveStreamingState = !this.state.liveStreamingEnabled;

        this.setState({ liveStreamingEnabled: newLiveStreamingState }, () => {
            if (!newLiveStreamingState) {
                // Remove all live streaming entries from streamEnabledWeb
                this.setState(prevState => {
                    return {
                        streamEnabledWeb: prevState.streamEnabledWeb.filter(item => item.isRec === true),
                        streamRows: [ { id: 1,
                            selectedOption: '',
                            textValue: '' } ]
                    };
                });
            }
        });
    };

    _onToggleStream: (title: string, toggle: boolean, isRec: boolean) => void;

    /**
   * Callback to be invoked when the user toggles the stream feature on or off.
   * @param {title} - Title of the Parameter
   * @param - {toggle} - Toggle Boolean
   * @returns {void}
   */
    _onToggleStream(title, toggle, isRec = false) {

        const checkIfKeyExisted = this.state.streamEnabledWeb.some(
      itm => itm.title === title
        );

        if (checkIfKeyExisted) {
            this.setState(
        {
            streamEnabledWeb: this.state.streamEnabledWeb.filter(
            itm => itm.title !== title
            )
        },
        () => logger.log('this.state', this.state)
            );

            return;
        }

        const pushKey = this.state.streamEnabledWeb;

        pushKey.push({
            title,
            toggle: typeof toggle === 'boolean' ? !toggle : true,
            open: false,
            value: undefined,
            isRec
        });
        this.setState(
      {
          streamEnabledWeb: pushKey
      },
      () => logger.log('this.state', this.state)
        );

    }

    /**
   * Override the disable logic to work with toggle states
   * @returns {boolean} True if submit should be disabled
   */
    _onDisable = () => {
        const isStorageFull = this._isStorageFullForSelectedRecordingService();

        // If neither recording nor live streaming is enabled, disable submit
        if (!this.state.recordingEnabled && !this.state.liveStreamingEnabled) {
            return true;
        }

        // If only recording is enabled, it's valid (recording doesn't need stream keys)
        if (this.state.recordingEnabled && !this.state.liveStreamingEnabled) {
            return isStorageFull;
        }

        // If live streaming is enabled, check if we have valid stream keys
        if (this.state.liveStreamingEnabled) {
            const hasValidLiveStream = this.state.streamRows.some(row =>
                row.selectedOption
        && row.textValue
        && row.textValue.trim() !== ''
            );

            if (!hasValidLiveStream) {
                return true;
            }
        }

        return this.state.recordingEnabled && isStorageFull;
    };

    /**
 * Override submit to sync streamEnabledWeb with toggle states
 * @returns {boolean} Result from parent submit
 */
    _onSubmit = () => {
        // COMPLETELY REBUILD streamEnabledWeb from scratch
        const updatedStreamEnabledWeb = [];

        // Add recording if enabled
        if (this.state.recordingEnabled && this.state.recTools && this.state.recTools.length > 0) {
            const recordingTool = this.state.recTools[0];

            updatedStreamEnabledWeb.push({
                title: recordingTool.title,
                toggle: true,
                open: false,
                value: undefined,
                isRec: true
            });
        }

        // Add live streaming entries if enabled
        if (this.state.liveStreamingEnabled) {
            this.state.streamRows.forEach(row => {
                if (row.selectedOption && row.textValue && row.textValue.trim() !== '') {
                    updatedStreamEnabledWeb.push({
                        title: row.selectedOption,
                        toggle: true,
                        open: false,
                        value: row.textValue,
                        isRec: false
                    });
                }
            });
        }

        this.setState({
            streamEnabledWeb: updatedStreamEnabledWeb,
            recordingO: this.state.recordingEnabled,
            dialogOpen: false // Close the dialog after submit
        });

        // Call parent submit method
        return super._onSubmit();
    };

    _onCancel: () => boolean;

    _onInitializeGoogleApi: () => void;

    /**
   * Loads the Google web client application used for fetching stream keys.
   * If the user is already logged in, then a request for available YouTube
   * broadcasts is also made.
   *
   * @private
   * @returns {void}
   */
    _onInitializeGoogleApi() {
        this.props
      .dispatch(loadGoogleAPI())
      .catch(response => this._parseErrorFromResponse(response));
    }

    /**
   * Extracts the stream key from a full RTMP URL
   * @param {string} url - The full RTMP URL
   * @returns {string} - The extracted stream key
   */
    _extractStreamKey = url => {
        if (!url || typeof url !== 'string') {
            return '';
        }

        // If it's already just a key (no protocol), return as is
        if (!url.includes('://') && !url.includes('/')) {
            return url;
        }

        // Extract the last part after the final slash
        const parts = url.split('/');

        return parts[parts.length - 1] || url;
    };

    /**
   * Parses platform name from API key format (e.g., "YouTube_Stream_Key" -> "Youtube")
   * @param {string} apiKey - The key from _autoLiveStreamKeys
   * @returns {string} - The platform name
   */
    _parsePlatformName = apiKey => {
        if (apiKey === 'Recording') {
            return 'Recording';
        }

        // Remove "_Stream_Key" or similar suffixes and capitalize properly
        const platformName = apiKey
      .replace(/_Stream_Key$/i, '')
      .replace(/_/g, ' ')
      .trim();

        // Capitalize first letter, rest lowercase (except for special cases)
        const formatted = platformName.charAt(0).toUpperCase() + platformName.slice(1).toLowerCase();

        // Handle special cases
        if (formatted.toLowerCase() === 'youtube') {
            return 'Youtube';
        }
        if (formatted.toLowerCase() === 'facebook') {
            return 'Facebook';
        }
        if (formatted.toLowerCase() === 'linkedin') {
            return 'LinkedIn';
        }
        if (formatted.toLowerCase() === 'twitch') {
            return 'Twitch';
        }
        if (formatted.toLowerCase() === 'instagram') {
            return 'Instagram';
        }
        if (formatted.toLowerCase() === 'vimeo') {
            return 'Vimeo';
        }

        return formatted;
    };

    /**
     * componentDidUpdate.
     */
    componentDidUpdate(previousProps) {
        super.componentDidUpdate(previousProps);

        if (
            previousProps._googleAPIState === GOOGLE_API_STATES.LOADED
      && this.props._googleAPIState === GOOGLE_API_STATES.SIGNED_IN
        ) {
            this._onGetYouTubeBroadcasts();
        }

        // Only process _autoLiveStreamKeys if it's defined and has changed
        if (this.props._autoLiveStreamKeys && !this.state.autofilldetailesreendered) {

            const { _autoLiveStreamKeys } = this.props;

            // Separate recording and live streaming keys
            const recordingEnabled = _autoLiveStreamKeys.Recording?.enable === true;

            const streamKeysFromApi = Object.keys(_autoLiveStreamKeys)
            .filter(key => {
                const item = _autoLiveStreamKeys[key];


                // Include only if enabled, has a key, and is not Recording
                return key !== 'Recording' && item && item.key && item.enable;
            })
            .map(key => {
                const platformName = this._parsePlatformName(key);
                const extractedKey = this._extractStreamKey(_autoLiveStreamKeys[key].key);

                return {
                    title: platformName,
                    value: extractedKey,
                    isRec: false,
                    toggle: true,
                    open: false
                };
            });

            // COMPLETELY REPLACE streamEnabledWeb with data from _autoLiveStreamKeys
            let updatedStreamEnabledWeb = [];

            // Add recording if enabled in _autoLiveStreamKeys
            if (recordingEnabled && this.state.recTools && this.state.recTools.length > 0) {
                const recordingTool = this.state.recTools[0];

                updatedStreamEnabledWeb.push({
                    title: recordingTool.title,
                    toggle: true,
                    open: false,
                    value: undefined,
                    isRec: true
                });
            }

            // Add ONLY the live streaming entries from _autoLiveStreamKeys
            updatedStreamEnabledWeb = [ ...updatedStreamEnabledWeb, ...streamKeysFromApi ];

            // Create rows for the UI based on _autoLiveStreamKeys data
            const rowsFromApi = streamKeysFromApi.length > 0
                ? streamKeysFromApi.map((stream, index) => {
                    return {
                        id: Date.now() + index,
                        selectedOption: stream.title,
                        textValue: stream.value
                    };
                })
                : [ { id: 1,
                    selectedOption: '',
                    textValue: '' } ];


            this.setState({
                streamEnabledWeb: updatedStreamEnabledWeb, // Replace entirely with API data
                streamRows: rowsFromApi,
                liveStreamingEnabled: streamKeysFromApi.length > 0,
                recordingEnabled,
                recordingO: recordingEnabled,
                autofilldetailesreendered: true
            });
        }
    }

    _onGetYouTubeBroadcasts: () => void;

    /**
   * Asks the user to sign in, if not already signed in, and then requests a
   * list of the user's YouTube broadcasts.
   *
   * @private
   * @returns {void}
   */
    _onGetYouTubeBroadcasts() {
        this.props
      .dispatch(updateProfile())
      .catch(response => this._parseErrorFromResponse(response));

        this.props
      .dispatch(requestAvailableYouTubeBroadcasts())
      .then(broadcasts => {
          this._setStateIfMounted({
              broadcasts
          });

          if (broadcasts.length === 1) {
              const broadcast = broadcasts[0];

              this._onYouTubeBroadcastIDSelected(broadcast.boundStreamID);
          }
      })
      .catch(response => this._parseErrorFromResponse(response));
    }

    _onGoogleSignIn: () => Object;

    /**
   * Forces the Google web client application to prompt for a sign in, such as
   * when changing account, and will then fetch available YouTube broadcasts.
   *
   * @private
   * @returns {Promise}
   */
    _onGoogleSignIn() {
        this.props
      .dispatch(signIn())
      .catch(response => this._parseErrorFromResponse(response));
    }

    _onRequestGoogleSignIn: () => Object;

    /**
   * Forces the Google web client application to prompt for a sign in, such as
   * when changing account, and will then fetch available YouTube broadcasts.
   *
   * @private
   * @returns {Promise}
   */
    _onRequestGoogleSignIn() {
    // when there is an error we show the google sign-in button.
    // once we click it we want to clear the error from the state
        this.props
      .dispatch(showAccountSelection())
      .then(() =>
          this._setStateIfMounted({
              broadcasts: undefined,
              errorType: undefined
          })
      )
      .then(() => this._onGetYouTubeBroadcasts());
    }

    _onYouTubeBroadcastIDSelected: (string) => Object;

    /**
   * Fetches the stream key for a YouTube broadcast and updates the internal
   * state to display the associated stream key as being entered.
   *
   * @param {string} boundStreamID - The bound stream ID associated with the
   * broadcast from which to get the stream key.
   * @private
   * @returns {Promise}
   */
    _onYouTubeBroadcastIDSelected(boundStreamID) {
        this.props
      .dispatch(requestLiveStreamsForYouTubeBroadcast(boundStreamID))
      .then(({ streamKey, selectedBoundStreamID }) =>
          this._setStateIfMounted({
              streamKey,
              selectedBoundStreamID
          })
      );
    }

    /**
   * Only show an error if an external request was made with the Google api.
   * Do not error if the login in canceled.
   * And searches in a Google API error response for the error type.
   *
   * @param {Object} response - The Google API response that may contain an
   * error.
   * @private
   * @returns {string|null}
   */
    _parseErrorFromResponse(response) {
        if (!response || !response.result) {
            return;
        }

        const result = response.result;
        const error = result.error;
        const errors = error && error.errors;
        const firstError = errors && errors[0];

        this._setStateIfMounted({
            errorType: (firstError && firstError.reason) || null
        });
    }

    /**
   * Renders a React Element for authenticating with the Google web client.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderYouTubePanel() {
        const { t, _googleProfileEmail } = this.props;
        const { broadcasts, selectedBoundStreamID } = this.state;

        let googleContent, helpText;

        switch (this.props._googleAPIState) {
        case GOOGLE_API_STATES.LOADED:
            googleContent = <GoogleSignInButton onClick = { this._onGoogleSignIn } />;
            helpText = t('liveStreaming.signInCTA');

            break;

        case GOOGLE_API_STATES.SIGNED_IN:
            if (broadcasts) {
                googleContent = (
                    <StreamKeyPicker
                        broadcasts = { broadcasts }
                        onBroadcastSelected = { this._onYouTubeBroadcastIDSelected }
                        selectedBoundStreamID = { selectedBoundStreamID } />
                );
            } else {
                googleContent = (<Spinner
                    isCompleting = { false }
                    size = 'medium' />);
            }

            /**
         * FIXME: Ideally this help text would be one translation string
         * that also accepts the anchor. This can be done using the Trans
         * component of react-i18next but I couldn't get it working...
         */
            helpText = (
                <div>
                    {`${t('liveStreaming.chooseCTA', { email: _googleProfileEmail })} `}
                    <a onClick = { this._onRequestGoogleSignIn }>
                        {t('liveStreaming.changeSignIn')}
                    </a>
                </div>
            );

            break;

        case GOOGLE_API_STATES.NEEDS_LOADING:
        default:
            googleContent = (<Spinner
                isCompleting = { false }
                size = 'medium' />);

            break;
        }

        if (this.state.errorType !== undefined) {
            googleContent
        = <GoogleSignInButton onClick = { this._onRequestGoogleSignIn } />
            ;
            helpText = this._getGoogleErrorMessageToDisplay();
        }

        return (
            <div className = 'google-panel'>
                <div className = 'live-stream-cta'>{helpText}</div>
                <div className = 'google-api'>{googleContent}</div>
            </div>
        );
    }

    _setStateIfMounted: (Object) => void;

    /**
   * Returns the error message to display for the current error state.
   *
   * @private
   * @returns {string} The error message to display.
   */
    _getGoogleErrorMessageToDisplay() {
        let text;

        switch (this.state.errorType) {
        case 'liveStreamingNotEnabled':
            text = this.props.t('liveStreaming.errorLiveStreamNotEnabled', {
                email: this.props._googleProfileEmail
            });
            break;
        default:
            text = this.props.t('liveStreaming.errorAPI');
            break;
        }

        return <div className = 'google-error'>{text}</div>;
    }
}

/**
 * Maps part of the Redux state to the component's props.
 *
 * @param {Object} state - The Redux state.
 * @returns {{
 *     _googleApiApplicationClientID: string
 * }}
 */
function _mapStateToProps(state: Object) {
    return {
        ..._abstractMapStateToProps(state),
        _googleApiApplicationClientID:
      state['features/base/config'].googleApiApplicationClientID
    };
}

export default translate(connect(_mapStateToProps)(StartLiveStreamDialog));
